import { useTranslation } from "@sg/i18n";
import { Badge, FileIcon, FolderOpenIcon, MonitorIcon, TradeIcon, UserIcon } from "@sg/ui";
import { truncate } from "lodash";
import { FunctionComponent, ReactNode, useMemo } from "react";
import { useQueryClient } from "react-query";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCurrentOrg, useOrgCounterparties } from "~/hooks/query";
import { useIsAuthorizedFn, useIsSuperAdmin } from "~/hooks/util/useRoles";
import { useSelectedEntityContext } from "~/hooks/util/useSelectedEntityContext";
import { ROUTE } from "~/routes/config";
import useAuthStore from "~/store/auth";
import useSettingPrefStore from "~/store/setttingPref";
import { RoleV2 } from "~/types";

export interface BaseSidebarItem {
  to?: string;
  name: string;
  className?: string;
  textClassName?: string;
  Icon?: FunctionComponent;
  disabled?: boolean;
  subheading?: string | null;
  action?: ReactNode;
  active?: boolean;
  id?: string;
  onClick?: () => void;
  core?: boolean;
}

const STATIC_PARAMS = ["orgId", "cpId"];

export interface SidebarItems extends BaseSidebarItem {
  subitems?: BaseSidebarItem[];
}

function NewBadge() {
  return (
    <Badge className="text-xs px-1 py-0.5" variant="constructive">
      New
    </Badge>
  );
}

function useSuperAdminSidebarItems() {
  const isSuperAdmin = useIsSuperAdmin();
  const { t } = useTranslation();

  return isSuperAdmin
    ? [
        {
          name: t("product_name"),
          to: "#",
          Icon: MonitorIcon,
          subitems: [
            { name: t("orgs"), to: ROUTE.ORGANIZATIONS },
            { name: t("onboarding"), to: ROUTE.ONBOARDING_REVIEW },
            { name: t("orders"), to: ROUTE.ORDERS },
            { name: t("positions"), to: ROUTE.INTERNAL_POSITIONS },
            { name: t("asset_master"), to: ROUTE.ASSET_MASTER },
            { name: t("global_admins"), to: ROUTE.USERS },
          ],
        },
      ]
    : [];
}

export function useSidebarItems() {
  const { t } = useTranslation();
  const authenticated = useAuthStore((state) => state.authenticated)();
  const superAdminItems = useSuperAdminSidebarItems();
  const selectedEntity = useSelectedEntityContext();
  const params = useParams();
  const { pathname } = useLocation();
  const setEntity = useSettingPrefStore((state) => state.setEntity);
  const navigate = useNavigate();
  const { data } = useOrgCounterparties();
  const queryClient = useQueryClient();
  const org = useCurrentOrg();

  const orgData = org?.data;
  const dynamicParams = useMemo(() => {
    return Object.keys(params).filter((r) => !STATIC_PARAMS.includes(r));
  }, [params]);

  const entities = useMemo(() => {
    return data?.sort((a, b) => a.name.localeCompare(b.name)) || [];
  }, [data]);

  const isAuthorized = useIsAuthorizedFn();

  const items = useMemo(() => {
    const entityData = selectedEntity?.data;

    function getSidebarItems<T>(roles: RoleV2[], item: T) {
      if (isAuthorized(roles)) {
        return [item];
      }

      return [];
    }

    if (!authenticated) return [];

    return [
      {
        name: entityData ? truncate(entityData.name, { length: 18 }) : t("entity_select"),
        to: "#",
        Icon: FolderOpenIcon,
        subheading: t("entities"),
        subitems: entities
          .filter((e) => !e.is_deactivated)
          .map((e) => ({
            name: e.name,
            id: e.id,
            active: e.id === entityData?.id,
            onClick: () => {
              if ("cpId" in params) {
                const shouldRedirect = dynamicParams.length > 0;
                queryClient.clear();
                if (shouldRedirect) {
                  navigate(
                    generatePath(ROUTE.PERPETUAL_FORWARDS, {
                      orgId: org.data?.id,
                      cpId: e.id,
                    }),
                  );
                } else {
                  navigate(pathname?.replace(`${params.cpId}`, e.id), { replace: true });
                }
              } else {
                setEntity(e.id);
              }
            },
          })),
      },
      ...getSidebarItems([RoleV2.TRADER], {
        name: t("trade"),
        to: "#",
        Icon: TradeIcon,
        subitems: [
          {
            name: t("forwards"),
            to: ROUTE.FORWARDS,
          },
          {
            name: t("perpetual_forwards"),
            to: ROUTE.PERPETUAL_FORWARDS,
            action: <NewBadge />,
            textClassName: "text-xs",
          },
        ],
      }),
      ...getSidebarItems([RoleV2.FINOPS, RoleV2.AUDITOR, RoleV2.TRADER], {
        name: t("positions"),
        to: ROUTE.ORGANIZATION_DETAILS_POSITIONS,
        Icon: FileIcon,
        core: true,
      }),
      ...getSidebarItems([RoleV2.FINOPS, RoleV2.AUDITOR], {
        name: t("finops"),
        to: "#",
        Icon: FileIcon,
        subitems: [
          { name: t("collateral"), to: ROUTE.COLLATERAL },
          { name: t("transfers"), to: ROUTE.TRANSFERS },
          { name: t("confirms"), to: ROUTE.COUNTERPARTY_DETAILS_CONFIRMS },
        ],
      }),
      ...getSidebarItems([RoleV2.ADMIN, RoleV2.AUDITOR, RoleV2.ONBOARDING], {
        name: t("admin"),
        to: "#",
        Icon: UserIcon,
        subitems: [
          { name: t("entities"), to: ROUTE.ORGANIZATION_DETAILS_COUNTERPARTIES },
          ...(isAuthorized([RoleV2.ADMIN, RoleV2.AUDITOR])
            ? [
                { name: t("users"), to: ROUTE.ORGANIZATION_DETAILS_USERS },
                { name: t("agreements"), to: ROUTE.COUNTERPARTY_DETAILS_AGREEMENTS },
              ]
            : []),
          { name: t("onboarding"), to: ROUTE.ORGANIZATION_ONBOARDING_REVIEW },
        ],
      }),
      ...superAdminItems,
    ];
  }, [
    selectedEntity?.data,
    authenticated,
    t,
    entities,
    superAdminItems,
    isAuthorized,
    params,
    dynamicParams.length,
    queryClient,
    navigate,
    org.data?.id,
    pathname,
    setEntity,
  ]);

  return { items, name: orgData ? orgData.display_name : t("org") };
}
